// frontend/lib/hooks/useNurseCredentials.ts
"use client";

import { useCallback, useEffect, useState } from "react";
import { apiGet, apiDelete, apiPut } from "../apiClient";

// =============================================================================
// Types (matching backend response shapes)
// =============================================================================

export interface CredentialType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_license: boolean;
  required_for_work: boolean;
  default_valid_years: number | null;
}

export type CredentialStatus = "PENDING" | "VERIFIED" | "REJECTED" | "EXPIRED";
export type ExpiryStatus = "VALID" | "EXPIRING_SOON" | "EXPIRED";

export interface NurseCredential {
  id: string;
  credential_type: CredentialType;
  license_number: string | null;
  issuing_state: string | null;
  issued_at: string | null;
  expires_at: string | null;
  document_url: string | null;
  status: CredentialStatus;
  expiry_status: ExpiryStatus;
  created_at: string;
  updated_at: string;
}

/**
 * Raw shape returned by GET /nurses/me/credentials today: a bare JSON array of
 * objects with FLAT credential_type fields (no nested credential_type object).
 * The full CredentialType metadata (is_license, required_for_work, etc.) is
 * fetched separately from /nurses/credential-types and merged client-side.
 */
export interface RawNurseCredential {
  id: string;
  credential_type_id: number;
  credential_type_code: string;
  credential_type_name: string;
  license_number: string | null;
  issuing_state: string | null;
  issued_at: string | null;
  expires_at: string | null;
  document_url: string | null;
  status: CredentialStatus;
  expiry_status: ExpiryStatus | null;
  created_at: string;
  updated_at?: string;
}

/**
 * Backend currently returns a bare JSON array; legacy/paginated variants may
 * wrap it as `{ items: [...] }`. Accept either and normalize.
 */
export type CredentialListResponse =
  | RawNurseCredential[]
  | { items?: RawNurseCredential[] | null };

export type CredentialTypesResponse =
  | CredentialType[]
  | { items?: CredentialType[] | null };

function normalizeArrayResponse<T>(
  data: T[] | { items?: T[] | null } | null | undefined,
): T[] {
  if (Array.isArray(data)) return data;
  return data?.items ?? [];
}

export function normalizeCredentialsResponse(
  data: CredentialListResponse | null | undefined,
): RawNurseCredential[] {
  return normalizeArrayResponse(data);
}

/**
 * Hydrate the nested credential_type object that downstream consumers expect.
 * Falls back to a minimal stub built from the flat fields when the catalog
 * doesn't have a matching entry, so consumers never read undefined fields.
 */
export function enrichCredential(
  raw: RawNurseCredential,
  typesById: Map<number, CredentialType>,
): NurseCredential {
  const fromCatalog = typesById.get(raw.credential_type_id);
  const credential_type: CredentialType = fromCatalog ?? {
    id: raw.credential_type_id,
    code: raw.credential_type_code,
    name: raw.credential_type_name,
    description: null,
    is_license: false,
    required_for_work: false,
    default_valid_years: null,
  };
  return {
    id: raw.id,
    credential_type,
    license_number: raw.license_number,
    issuing_state: raw.issuing_state,
    issued_at: raw.issued_at,
    expires_at: raw.expires_at,
    document_url: raw.document_url,
    status: raw.status,
    expiry_status: raw.expiry_status ?? "VALID",
    created_at: raw.created_at,
    updated_at: raw.updated_at ?? raw.created_at,
  };
}

// =============================================================================
// Credentials Hook
// =============================================================================

interface UseNurseCredentialsOptions {
  statusFilter?: CredentialStatus;
}

export function useNurseCredentials(options: UseNurseCredentialsOptions = {}) {
  const { statusFilter } = options;

  const [credentials, setCredentials] = useState<NurseCredential[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCredentials = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) {
        params.set("credential_status", statusFilter);
      }
      const queryString = params.toString();
      const credentialsPath = `/nurses/me/credentials${queryString ? `?${queryString}` : ""}`;
      // Fetch credentials and the credential-type catalog in parallel; enrich
      // raw flat credentials into the nested shape consumers expect.
      const [credsResp, typesResp] = await Promise.all([
        apiGet<CredentialListResponse>(credentialsPath),
        apiGet<CredentialTypesResponse>("/nurses/credential-types").catch(() => [] as CredentialType[]),
      ]);
      const raws = normalizeCredentialsResponse(credsResp);
      const types = normalizeArrayResponse<CredentialType>(typesResp);
      const typesById = new Map(types.map((t) => [t.id, t]));
      setCredentials(raws.map((r) => enrichCredential(r, typesById)));
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  return {
    credentials,
    isLoading,
    error,
    refetch: fetchCredentials,
  };
}

// =============================================================================
// Credential Types Hook
// =============================================================================

interface UseCredentialTypesOptions {
  isLicense?: boolean;
  requiredForWork?: boolean;
}

export function useCredentialTypes(options: UseCredentialTypesOptions = {}) {
  const { isLicense, requiredForWork } = options;

  const [credentialTypes, setCredentialTypes] = useState<CredentialType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCredentialTypes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (isLicense !== undefined) {
        params.set("is_license", String(isLicense));
      }
      if (requiredForWork !== undefined) {
        params.set("required_for_work", String(requiredForWork));
      }
      const queryString = params.toString();
      const path = `/nurses/credential-types${queryString ? `?${queryString}` : ""}`;
      const data = await apiGet<{ items: CredentialType[] }>(path);
      setCredentialTypes(data.items);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  }, [isLicense, requiredForWork]);

  useEffect(() => {
    fetchCredentialTypes();
  }, [fetchCredentialTypes]);

  return {
    credentialTypes,
    isLoading,
    error,
    refetch: fetchCredentialTypes,
  };
}

// =============================================================================
// Computed Stats Helper
// =============================================================================

export interface CredentialStats {
  total: number;
  verified: number;
  pending: number;
  rejected: number;
  expiringSoon: number;
  expired: number;
}

export function computeCredentialStats(
  credentials: NurseCredential[] | undefined | null,
): CredentialStats {
  return (credentials ?? []).reduce(
    (stats, cred) => {
      stats.total++;

      // Status counts
      if (cred.status === "VERIFIED") stats.verified++;
      else if (cred.status === "PENDING") stats.pending++;
      else if (cred.status === "REJECTED") stats.rejected++;

      // Expiry counts (for verified credentials)
      if (cred.expiry_status === "EXPIRING_SOON") stats.expiringSoon++;
      else if (cred.expiry_status === "EXPIRED") stats.expired++;

      return stats;
    },
    { total: 0, verified: 0, pending: 0, rejected: 0, expiringSoon: 0, expired: 0 }
  );
}

// =============================================================================
// Credential Mutations
// =============================================================================

export interface CredentialUploadData {
  credential_type_id: number;
  license_number?: string;
  issuing_state?: string;
  issued_at?: string;
  expires_at?: string;
  issuing_authority?: string;
}

/**
 * Upload a new credential with file and metadata.
 * Returns progress percentage (0-100) via onProgress callback.
 */
export async function uploadCredential(
  file: File,
  metadata: CredentialUploadData,
  onProgress?: (progress: number) => void
): Promise<NurseCredential> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("credential_type_id", String(metadata.credential_type_id));
    if (metadata.license_number) formData.append("license_number", metadata.license_number);
    if (metadata.issuing_state) formData.append("issuing_state", metadata.issuing_state);
    if (metadata.issued_at) formData.append("issued_at", metadata.issued_at);
    if (metadata.expires_at) formData.append("expires_at", metadata.expires_at);
    if (metadata.issuing_authority) formData.append("issuing_authority", metadata.issuing_authority);

    const xhr = new XMLHttpRequest();

    // Upload progress
    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        }
      });
    }

    // Success
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve(data);
        } catch (_err) {
          reject(new Error("Failed to parse response"));
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.detail || `Upload failed with status ${xhr.status}`));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    });

    // Error
    xhr.addEventListener("error", () => {
      reject(new Error("Network error during upload"));
    });

    // Abort
    xhr.addEventListener("abort", () => {
      reject(new Error("Upload aborted"));
    });

    // Get auth token
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
    const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000/api/v1";

    xhr.open("POST", `${API_BASE_URL}/nurses/me/credentials`);
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    xhr.send(formData);
  });
}

/**
 * Update credential metadata.
 */
export async function updateCredentialMetadata(
  credentialId: string,
  metadata: Partial<CredentialUploadData>
): Promise<NurseCredential> {
  return apiPut<NurseCredential>(`/nurses/me/credentials/${credentialId}`, metadata);
}

/**
 * Delete a credential.
 */
export async function deleteCredential(credentialId: string): Promise<void> {
  return apiDelete<void>(`/nurses/me/credentials/${credentialId}`);
}
